/**
 * HANDMATIGE volgorde-/zichtbaarheids-overrides voor de catalogus — de tegenhanger van het
 * GEGENEREERDE `timmers-order.ts`. Dit bestand wordt NOOIT door een sync overschreven, dus
 * alles wat je hier zet blijft staan na elke `npm run sync:all`.
 *
 * `SOORT_COLOR_ORDER` — de volgorde van de kleur-/uitvoering-chips binnen één glassoort
 *   (configurator). Sleutel = de subcategorie (soort) zoals in de catalogus ("Floatglas").
 *   Kleuren die je hier niet noemt, schuiven achteraan (in hun bestaande volgorde) — zo
 *   verdwijnt een nieuwe Timmers-kleur nooit, hij komt alleen onderaan te staan.
 *
 * `SOORT_COLOR_HIDE` — kleuren die niet bij deze soort horen en dus niet getoond worden
 *   (bv. een verdwaalde/dubbele feed-entry die eigenlijk onder een andere soort valt).
 */

export const SOORT_COLOR_ORDER: Record<string, string[]> = {
  "Spiegels op maat": [
    "Blank verzilverd",
    "Blank extra helder verzilverd",
    "Acide blank verzilverd",
    "Brons verzilverd",
    "Grijs verzilverd",
    "Antiek verzilverd",
    "Antiek verzilverd brons",
    "Champagne verzilverd",
    "Rosé verzilverd",
    "Blauw verzilverd",
    "Goud verzilverd",
    "Grijs verzilverd confrontatie",
    "Gelaagd 33.1 verzilverd 2 zijdig",
    "Blank verzilverd + veiligheidsfolie",
    "Brons verzilverd + veiligheidsfolie",
    "Grijs verzilverd + veiligheidsfolie",
  ],
  Floatglas: [
    "Floatglas blank",
    "Floatglas extra helder",
    "Floatglas ontspiegeld",
    "Floatglas 2 zijdig ontspiegeld",
    "Floatglas brons",
    "Floatglas groen",
    "Floatglas grijs",
    "Floatglas blauw",
    "Floatglas dark grey",
    "Floatglas Mirastar",
    "Floatglas Mirropane chrome",
    "Floatglas Mirropane chrome plus",
  ],
};

export const SOORT_COLOR_HIDE: Record<string, string[]> = {
  // "Floatglas Masterpoint" hoort bij figuurglas (staat daar al als "Masterpoint blank").
  Floatglas: ["Floatglas Masterpoint"],
};

/**
 * HANDMATIGE volgorde van de productgroepen (subcategorieën) binnen een artikelcategorie —
 * wint van de Timmers-volgorde (`GROEP_ORDER` in timmers-order.ts). Sleutel = categorie-slug,
 * waarde = groepsnamen in de gewenste volgorde. Niet-genoemde groepen schuiven achteraan (op
 * aantal), zodat een nieuwe Timmers-groep nooit verdwijnt. Sync-veilig (dit bestand wordt niet
 * overschreven). Toegepast via `getCategoryGroepen`/`getCategoryTree`/mega-menu.
 */
export const GROEP_ORDER_OVERRIDE: Record<string, string[]> = {
  "douche-en-bad": [
    "Schuif- & inloopdouche",
    "Badkamerspiegels",
    "Scharnieren",
    "Glasklemmen",
    "Knoppen & Grepen",
    "Stabilisatie",
    "U-profielen & montageprofielen",
    "Afdichtprofielen",
    "Handdoekstang",
    "Sauna",
    "Overige",
  ],
  "interieurbeglazing": [
    "Deurkader",
    "Profielen",
    "Overkappingssystemen",
    "Aanslagdeuren",
    "Grepen",
    "Puien",
    "Schuifdeuren",
    "Pendeldeuren",
  ],
  "vitrine-en-meubelbeslag": [
    "Scharnieren",
    "Knoppen & Grepen",
    "Glasplaatdragers",
    "Sloten",
    "Opschuifsloten",
    "Schuifruitenbeslag",
    "Draaischoenen",
  ],
  "balustrade": [
    "Profielen",
    "Leuningen",
    "Glasklemmen",
    "Punthouders",
    "Frans balkon",
    "Montagetools",
  ],
  "glasartikelen": [
    "Gereedschappen",
    "Kitten",
    "Glaslatten",
    "Glas snijden en glas breken",
    "Boren, slijpen & polijsten",
    "Klimmaterialen",
    "Banden en Tapes",
    "Beglazingsblokjes",
    "Voorzetramen",
    "Spiegelbenodigdheden",
    "Glasreiniging en oppervlakte bescherming",
    "Morso Verstekstansen",
    "Diversen",
  ],
};

/** Handmatige rang van een groep (of -1 als er geen override voor deze categorie is). */
export function groepOverrideRank(category: string, groep: string): number {
  const order = GROEP_ORDER_OVERRIDE[category];
  if (!order) return -1;
  const i = order.indexOf(groep);
  return i === -1 ? 9999 : i; // niet-genoemd → achteraan (maar wél ná de genoemde)
}

/** Volgorde van de "Basisglas"-soorttegels op /glas-op-maat (op subcategorie-naam). */
export const BASISGLAS_SOORT_ORDER: string[] = [
  "Floatglas",
  "Mat glas",
  "Figuurglas",
  "Draadglas",
  "Spiegels op maat",
  "Installatieglas",
];

/** Sorteert items op een lijst van namen; niet-genoemde behouden hun volgorde en komen achteraan. */
export function orderBy<T>(order: string[], key: (t: T) => string, items: T[]): T[] {
  const rank = (s: string): number => {
    const i = order.indexOf(s);
    return i < 0 ? order.length : i;
  };
  return items
    .map((t, i) => ({ t, i }))
    .sort((a, b) => rank(key(a.t)) - rank(key(b.t)) || a.i - b.i)
    .map((x) => x.t);
}

/**
 * Past de handmatige volgorde + verberg-lijst toe op een kleurenlijst van één soort.
 * Niet-genoemde kleuren behouden hun onderlinge volgorde en komen achteraan.
 */
export function orderColors(subcategory: string, colors: string[]): string[] {
  const hide = SOORT_COLOR_HIDE[subcategory];
  const visible = hide ? colors.filter((c) => !hide.includes(c)) : colors;
  const order = SOORT_COLOR_ORDER[subcategory];
  if (!order) return visible;
  const rank = (c: string): number => {
    const i = order.indexOf(c);
    return i < 0 ? order.length : i; // niet-genoemd → achteraan
  };
  return visible
    .map((c, i) => ({ c, i })) // index bewaren voor een stabiele sort van de rest
    .sort((a, b) => rank(a.c) - rank(b.c) || a.i - b.i)
    .map((x) => x.c);
}
